import type { KeyboardEvent, RefObject, JSX } from 'react'

import { clsx } from 'clsx'

import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import { Textarea } from '@buffer-mono/popcorn'

import type { Service } from '~publish/pages/Community/graphqlEnums'

import { EngagementInsertedTextHighlight } from '../EngagementInsertedTextHighlight'
import type { InsertedRange } from '../EngagementInsertedTextHighlight'
import { useReplyFormSurface } from '../ReplyForm/ReplyFormContext'
import type { ReplyFormSurface } from '../ReplyForm/ReplyFormContext'

import styles from './EngagementTextarea.module.css'

const SURFACE_CLASS: Record<ReplyFormSurface, string> = {
  subtle: styles.subtle,
  card: styles.card,
}

type EngagementTextareaProps = {
  id?: string
  textareaRef: RefObject<HTMLTextAreaElement>
  replyText: string
  setReplyText: (text: string) => void
  error?: string
  insertedRange?: InsertedRange | null
  onHighlightComplete?: () => void
  maxLength: number
  service: Service
  ariaLabel: string
  /** Placeholder copy. Defaults to the generic reply prompt. */
  placeholder?: string
  /** Visible lines of text before the field scrolls. */
  rows?: number
  /** Holds the text in place, keeping focus, e.g. while it is being sent. */
  readOnly?: boolean
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
}

/**
 * The reply form's field, drawn for the surface its `ReplyForm.Root` sets.
 * Controlled — the text and inserted-range state live in the per-feature
 * reply hooks. Inserted
 * AI-suggestion / saved-reply text is highlighted via the scroll-synced
 * `EngagementInsertedTextHighlight` overlay: `background-clip: text` can't
 * apply to a `<textarea>`, so the gradient lives on an overlay span mirroring
 * the textarea text while the textarea glyphs go transparent (`.highlighting`).
 */
export const EngagementTextarea = ({
  id,
  textareaRef,
  replyText,
  setReplyText,
  error,
  insertedRange,
  onHighlightComplete,
  maxLength,
  service,
  ariaLabel,
  placeholder = 'Type in your reply...',
  rows = 4,
  readOnly,
  onKeyDown,
}: EngagementTextareaProps): JSX.Element => {
  const surface = useReplyFormSurface()
  const hasHighlight =
    !!insertedRange &&
    insertedRange.end <= replyText.length &&
    insertedRange.start < insertedRange.end

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value
    const length = getCharsCountForService(newValue, service)

    // Only update if the new value doesn't exceed maxLength
    if (length <= maxLength) {
      setReplyText(newValue)
      // User edits invalidate the highlighted range (indices would shift),
      // so stop painting the gradient.
      if (insertedRange) onHighlightComplete?.()
    }
  }

  return (
    <div className={styles.textareaWrapper}>
      <Textarea
        id={id}
        ref={textareaRef}
        name="replyText"
        placeholder={placeholder}
        rows={rows}
        value={replyText}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        readOnly={readOnly}
        required
        className={clsx(
          styles.textarea,
          SURFACE_CLASS[surface],
          error && styles.error,
          hasHighlight && styles.highlighting,
        )}
        resize="none"
        aria-label={ariaLabel}
        aria-required
        aria-invalid={!!error}
        aria-describedby={error && id ? `${id}-error` : undefined}
      />
      {hasHighlight && insertedRange && (
        <EngagementInsertedTextHighlight
          textareaRef={textareaRef}
          text={replyText}
          range={insertedRange}
          onComplete={onHighlightComplete}
        />
      )}
    </div>
  )
}

EngagementTextarea.displayName = 'EngagementTextarea'
